import 'react'
import { Link } from 'react-router-dom'
import {
  Handshake,
  Truck,
  PackageCheck,
  ShieldCheck,
  Eye,
  Gem,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Phone,
  Target,
  Rocket,
  MessageCircle,
  CheckCircle2,
} from 'lucide-react'
import { useApp } from '../context/app-context'
import { t } from '../i18n'
import SEO from '../components/SEO'

const WHATSAPP_URL = 'https://wa.me/201028137221'

type Step = { icon: React.ReactNode; num: string; title: string; desc: string }

export default function About() {
  const { lang } = useApp()
  const isAr = lang === 'ar'
  const Arrow = isAr ? ArrowLeft : ArrowRight

  // Static, crawlable stats (no count-up gimmick)
  const statCards = [
    { value: '10+', label: t(lang, 'stat1') },
    { value: '500+', label: t(lang, 'stat2') },
    { value: '10,000+', label: t(lang, 'stat3') },
    { value: '50+', label: t(lang, 'stat4') },
  ]

  const JOURNEY: Step[] = [
    { icon: <Handshake size={22} aria-hidden="true" />, num: '01', title: t(lang, 'step1Title'), desc: t(lang, 'step1Desc') },
    { icon: <Truck size={22} aria-hidden="true" />, num: '02', title: t(lang, 'step2Title'), desc: t(lang, 'step2Desc') },
    { icon: <PackageCheck size={22} aria-hidden="true" />, num: '03', title: t(lang, 'step3Title'), desc: t(lang, 'step3Desc') },
  ]

  const VALUES = [
    { icon: <ShieldCheck size={22} aria-hidden="true" />, title: t(lang, 'val1'), desc: t(lang, 'val1Desc') },
    { icon: <Eye size={22} aria-hidden="true" />, title: t(lang, 'val2'), desc: t(lang, 'val2Desc') },
    { icon: <Gem size={22} aria-hidden="true" />, title: t(lang, 'val3'), desc: t(lang, 'val3Desc') },
    { icon: <Sparkles size={22} aria-hidden="true" />, title: t(lang, 'val4'), desc: t(lang, 'val4Desc') },
  ]

  return (
    <main className="page about-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO title={t(lang, 'aboutHeroTitle')} description={t(lang, 'aboutHeroDesc')} url="https://shelil-logistics-services.pages.dev/about" />

      {/* ===== HERO ===== */}
      <section className="about-hero page-rise">
        <div
          className="about-hero-bg"
          style={{ backgroundImage: 'url(/assets/images/hero-about.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
          aria-hidden="true"
        />
        <div className="about-hero-inner">
          <div className="about-hero-logo-wrap">
            <img src="/logo.png" alt={isAr ? 'شليل للخدمات اللوجستية' : 'Shalil Logistics'} className="about-hero-logo" width={64} height={64} />
            <div>
              <span className="about-hero-word">شليل</span>
              <span className="about-hero-main">Logistics</span>
            </div>
          </div>
          <h1 className="about-hero-title">{t(lang, 'aboutHeroTitle')}</h1>
          <p className="about-hero-desc">{t(lang, 'aboutHeroDesc')}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <Link to="/contact" className="about-cta-btn">{t(lang, 'freeConsult')} <Arrow size={18} aria-hidden="true" /></Link>
            <a href="#journey" className="btn btn-outline">{isAr ? 'رحلة الشحنة' : 'The Journey'}</a>
          </div>
        </div>
      </section>

      {/* ===== MISSION & VISION ===== */}
      <section className="about-block">
        <div className="about-block-inner">
          <hr className="about-section-divider" aria-hidden="true" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            <div className="mv-card" style={{ position: 'relative' }}>
              <div className="mv-icon"><Target size={28} aria-hidden="true" /></div>
              <span className="mv-tag">{isAr ? 'مهمتنا' : 'Our Mission'}</span>
              <h2>{t(lang, 'missionTitle')}</h2>
              <p>{t(lang, 'missionText')}</p>
            </div>
            <div className="mv-card" style={{ position: 'relative' }}>
              <div className="mv-icon"><Rocket size={28} aria-hidden="true" /></div>
              <span className="mv-tag">{isAr ? 'رؤيتنا' : 'Our Vision'}</span>
              <h2>{t(lang, 'visionTitle')}</h2>
              <p>{t(lang, 'visionText')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="about-stats" aria-label={t(lang, 'statsTitle')}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div className="about-stat-row">
            {statCards.map((s, i) => (
              <div key={i} className="about-stat page-rise" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="about-stat-num">{s.value}</div>
                <div className="about-stat-desc">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== JOURNEY ===== */}
      <section id="journey" className="about-block">
        <div className="about-block-inner">
          <span className="about-block-label">{isAr ? 'كيف نعمل' : 'How we work'}</span>
          <h2 className="about-block-title">{t(lang, 'journeyTitle')}</h2>
          <div className="about-step-grid">
            {JOURNEY.map((step, i) => (
              <div key={i} className={`about-step page-rise${i % 2 === 1 ? ' about-step--right' : ''}`} style={{ animationDelay: `${i * 100}ms` }}>
                <div className="about-step-icon-wrap" aria-hidden="true">{step.icon}</div>
                <div className="about-step-content">
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, letterSpacing: '0.15em', color: 'var(--accent)', display: 'block', marginBottom: 6, fontWeight: 700 }}>
                    STEP {step.num}
                  </span>
                  <strong>{step.title}</strong>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section className="about-block" style={{ background: 'var(--bg-section)' }}>
        <div className="about-block-inner">
          <span className="about-block-label">{isAr ? 'ما يميزنا' : 'What sets us apart'}</span>
          <h2 className="about-block-title">{t(lang, 'valuesTitle')}</h2>
          <div className="about-values-grid">
            {VALUES.map((v, i) => (
              <div key={i} className="about-value page-rise" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="about-value-icon" aria-hidden="true">{v.icon}</div>
                <strong>{v.title}</strong>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COVERAGE STRIP ===== */}
      <section className="about-block">
        <div className="about-block-inner">
          <span className="about-block-label">{isAr ? 'أين نصل' : 'Where we reach'}</span>
          <h2 className="about-block-title">{isAr ? 'موانئ ومنافذ رئيسية' : 'Key Ports & Crossings'}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {['ميناء دمياط', 'ميناء الإسكندرية', 'العين السخنة', 'ميناء سفاجا', 'نويبع', 'العقبة', 'السلوم', 'قسطل', 'السادس من أكتوبر', 'العامرية'].map(label => (
              <span
                key={label}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: '1px solid var(--border)',
                  background: 'var(--bg-card)', borderRadius: 50, fontSize: 13, fontWeight: 600, color: 'var(--text)', boxShadow: 'var(--shadow-sm)'
                }}
              >
                <CheckCircle2 size={14} color="var(--accent)" aria-hidden="true" />{label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="about-cta">
        <div className="about-cta-inner" style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(1.8rem, 4.8vw, 2.4rem)', lineHeight: 1.2, color: '#fff', letterSpacing: '-0.02em', margin: 0 }}>
            {isAr ? 'هل أنت مستعد لنقل بضاعتك؟' : 'Ready to move your cargo?'}
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(255,255,255,0.9)', maxWidth: '60ch', margin: 0 }}>
            {isAr
              ? 'فريق شليل جاهز لحجز شحنتك خلال أقل من ساعة. استشارة مجانية لكل استفسار.'
              : 'Shalil team can book your shipment in under an hour. Free consultation for every inquiry.'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/contact" className="about-cta-btn">{t(lang, 'freeConsult')} <Arrow size={18} aria-hidden="true" /></Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp-big" style={{ textDecoration: 'none' }}>
              <MessageCircle size={18} aria-hidden="true" /> WhatsApp
            </a>
            <a href="tel:+201028137221" className="btn-outline-contact" style={{ textDecoration: 'none' }}>
              <Phone size={18} aria-hidden="true" /> {isAr ? 'اتصل الآن' : 'Call Now'}
            </a>
          </div>
        </div>
      </section>
    </main>
  )
}
